import {z as r} from 'zod';

import {getEmbedVideoMetadataJsonUrl} from './review-embed-video-helper';

const embedVideoThumbnailSchema = r.object({
    url: r.string().url(),
    max_width: r.number().optional(),
});

const embedVideoContentSchema = r.object({
    thumbnail: r.string().url().optional(),
    thumbnails: r.array(embedVideoThumbnailSchema).optional(),
});

const embedVideoResponseSchema = r.object({
    content: embedVideoContentSchema,
});

type EmbedVideoMetadataType = {
    videoUrl: string;
    thumbnailUrl: string | null;
};

const metadataCache = new Map<string, Promise<EmbedVideoMetadataType | null>>();

const GALLERY_THUMBNAIL_TARGET_WIDTH = 480;

const PLAYER_PAGE_THUMBNAIL_PATTERNS: Array<RegExp> = [
    /property="og:image:secure_url"\s+content="([^"]+)"/i,
    /property="og:image"\s+content="([^"]+)"/i,
    /name="twitter:image:src"\s+content="([^"]+)"/i,
    /<link\s+rel="image_src"\s+href="([^"]+)"/i,
];

function normalizeThumbnailUrl(raw: string | null | undefined): string | null {
    if (!raw) {
        return null;
    }

    const trimmed = raw
        .trim()
        .replace(/^https?:https?:/i, 'https:')
        .replace(/^http:https:/i, 'https:');

    try {
        return new URL(trimmed).toString();
    } catch {
        return null;
    }
}

function pickGalleryThumbnailUrl(content: r.infer<typeof embedVideoContentSchema>): string | null {
    if (content.thumbnail) {
        return normalizeThumbnailUrl(content.thumbnail);
    }

    const thumbnails = content.thumbnails;

    if (!thumbnails?.length) {
        return null;
    }

    const sortedByWidth = [...thumbnails].sort((left, right) => (left.max_width ?? 0) - (right.max_width ?? 0));
    const preferred =
        sortedByWidth.find((item) => (item.max_width ?? 0) >= GALLERY_THUMBNAIL_TARGET_WIDTH) ?? sortedByWidth.at(-1);

    return normalizeThumbnailUrl(preferred?.url);
}

function parseThumbnailFromPlayerHtml(html: string): string | null {
    const thumbnailUrl = PLAYER_PAGE_THUMBNAIL_PATTERNS.map((pattern) =>
        normalizeThumbnailUrl(pattern.exec(html)?.[1])
    ).find((url): url is string => url !== null);

    return thumbnailUrl ?? null;
}

function parseEmbedVideoMetadataFromJson(videoUrl: string, payload: unknown): EmbedVideoMetadataType | null {
    const parsed = embedVideoResponseSchema.safeParse(payload);

    if (!parsed.success) {
        return null;
    }

    const thumbnailUrl = pickGalleryThumbnailUrl(parsed.data.content);

    if (!thumbnailUrl) {
        return null;
    }

    return {
        videoUrl,
        thumbnailUrl,
    };
}

async function loadEmbedVideoMetadataFromResponse(
    videoUrl: string,
    response: Response
): Promise<EmbedVideoMetadataType | null> {
    const contentType = response.headers.get('content-type') ?? '';

    if (contentType.includes('application/json')) {
        const payload: unknown = await response.json();

        return parseEmbedVideoMetadataFromJson(videoUrl, payload);
    }

    const html = await response.text();
    const thumbnailUrl = parseThumbnailFromPlayerHtml(html);

    if (!thumbnailUrl) {
        return null;
    }

    return {
        videoUrl,
        thumbnailUrl,
    };
}

export function fetchEmbedVideoMetadata(videoUrl: string): Promise<EmbedVideoMetadataType | null> {
    const metadataUrl = getEmbedVideoMetadataJsonUrl(videoUrl);

    if (!metadataUrl) {
        return Promise.resolve(null);
    }

    const cached = metadataCache.get(videoUrl);

    if (cached) {
        return cached;
    }

    const request = fetch(metadataUrl, {credentials: 'omit', method: 'GET'})
        .then((response) => {
            if (!response.ok) {
                throw new Error(`Embed video metadata request failed: ${response.status}`);
            }

            return loadEmbedVideoMetadataFromResponse(videoUrl, response);
        })
        .catch(() => null);

    metadataCache.set(videoUrl, request);

    return request;
}
